import React from "react";
import { motion } from "framer-motion";

const TermsandConditions = () => {
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2,
      },
    },
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0, 1] },
    },
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5 },
    },
  };

  const sections = [
    {
      title: "Service Nature",
      content:
        "SalaryMates provides structured financial support assistance designed for salaried individuals facing short-term financial requirements. The platform offers assessment-based support solutions and guidance for eligible users.",
    },
    {
      title: "User Responsibility",
      content: [
        "Information submitted by you is accurate and complete",
        "Documents shared are genuine and valid",
        "You are authorized to provide the submitted details",
      ],
      isList: true,
    },
    {
      title: "Application Review",
      content:
        "All requests are subject to internal review and verification. Submission of details does not guarantee approval or access to support services.",
    },
    {
      title: "Communication Consent",
      content:
        "By sharing your details, you agree to receive communication through calls, SMS, email, WhatsApp, or digital channels for service-related updates and assistance.",
    },
    {
      title: "Charges & Pricing",
      content:
        "Applicable charges, fees, or service costs (if any) will always be communicated clearly before proceeding. No hidden charges are applied without prior disclosure.",
    },
    {
      title: "Fair Usage",
      content:
        "Users must not misuse the platform, submit false information, or attempt unauthorized activities. Violation may result in immediate suspension of services.",
    },
    {
      title: "Service Modification",
      content:
        "SalaryMates reserves the right to update, modify, suspend, or improve any part of its services without prior notice where required. We strive to notify users of significant changes where possible.",
    },
    {
      title: "Limitation of Liability",
      content:
        "SalaryMates shall not be responsible for losses arising from incorrect information submitted by users, delays caused by incomplete verification, or external technical disruptions beyond our control.",
    },
    {
      title: "Policy Updates",
      content:
        "Terms may be revised periodically to reflect operational or regulatory changes. Continued use of the platform implies acceptance of updated terms.",
    },
  ];

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* Dark gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0A1620]"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* Wave decorations */}
        <svg className="absolute top-0 left-0 w-full h-48 rotate-180" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>

        <svg className="absolute bottom-0 right-0 w-full h-48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                LEGAL AGREEMENT • TERMS OF SERVICE
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Terms & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">Conditions</span>
            </h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              Please read these terms carefully before using our services. By
              accessing our platform, you agree to be bound by these terms.
            </motion.p>
          </motion.div>

          {/* Content Sections */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="space-y-6"
          >
            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-2xl border border-[#2D5F7A]/30 overflow-hidden hover:border-[#7BC4E0]/50 transition-all duration-300"
              >
                <div className="border-l-4 border-[#7BC4E0] p-6 md:p-8">
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-4">
                    {section.title}
                  </h2>
                  {section.isList ? (
                    <ul className="space-y-3">
                      {section.content.map((item, itemIdx) => (
                        <motion.li
                          key={itemIdx}
                          variants={fadeInRight}
                          className="flex items-start gap-3 text-[#CCE6F5]"
                        >
                          <span className="text-[#7BC4E0] font-bold mt-1">•</span>
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[#CCE6F5] leading-relaxed">
                      {section.content}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Strong Closing Statement */}
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-[#1E3B4A] to-[#0F2128] rounded-2xl border border-[#2D5F7A]/30 overflow-hidden"
            >
              <div className="p-6 md:p-8 text-center">
                
                <p className="text-[#CCE6F5] text-lg md:text-xl font-medium italic">
                  "Our goal is to maintain a transparent, respectful, and secure
                  experience for every working professional who chooses SalaryMates."
                </p>
              </div>
            </motion.div>

            
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative text */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">FAIR</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">TRANSPARENT</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">ACCOUNTABLE</span>
      </div>
    </section>
  );
};

export default TermsandConditions;